import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, Clock, Receipt, Printer, CreditCard, ShieldCheck,
  Money,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicEbill() {
  const { invoiceNo } = useParams();
  const [bill, setBill] = useState(null);
  const [err, setErr] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => axios.get(`${API}/ebill/${invoiceNo}`, {
    params: { _: Date.now() },  // cache-buster — invoice content can change
    headers: { "Cache-Control": "no-cache" },
  })
    .then(r => setBill(r.data))
    .catch(e => setErr(e.response?.data?.detail || "Could not load invoice"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceNo]);

  // Tab title — never "Emergent"
  useEffect(() => {
    if (bill) {
      document.title = `Invoice ${bill.invoice_no} · ${bill.tenant?.name || "Rescigo Health"}`;
    } else {
      document.title = "e-Bill · Rescigo Health";
    }
  }, [bill]);

  if (err) return <Center>{err}</Center>;
  if (!bill) return <Center>Loading…</Center>;

  const paid = bill.payment_status === "paid";
  const dueNow = bill.balance_due ?? bill.total ?? 0;
  const showPayNow = !paid && dueNow > 0;

  const loadRzpScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const payNow = async () => {
    setPaying(true);
    try {
      const ok = await loadRzpScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load payment SDK. Check your connection.");
        setPaying(false);
        return;
      }
      const { data: order } = await axios.post(
        `${API}/payments/razorpay/public-order`,
        { invoice_no: invoiceNo },
      );
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: bill.tenant?.name || "Rescigo Health",
        description: `Invoice ${order.invoice_no}`,
        order_id: order.order_id,
        prefill: order.prefill || {},
        notes: order.notes || {},
        theme: { color: "#2D5A27" },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.message("Payment window closed");
          },
        },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/razorpay/public-verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful");
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Verification failed");
          } finally {
            setPaying(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "unknown"}`);
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F4] py-10 px-4 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto bg-white border border-[#E5E2D9] rounded-md shadow-sm print:shadow-none print:border-none">
        {/* Header */}
        <div className="px-8 py-6 border-b border-[#E5E2D9] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Tax Invoice / e-Bill</div>
            <div className="font-mono text-2xl font-medium mt-1">{bill.invoice_no}</div>
            <div className="text-xs text-[#5C5A55] mt-1">{bill.date}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-xl text-[#2D5A27]">{bill.tenant?.name || "Rescigo Health"}</div>
            {bill.tenant?.gstin && <div className="text-[10px] text-[#5C5A55] mt-1 font-mono">GSTIN · {bill.tenant.gstin}</div>}
            {bill.tenant?.address && <div className="text-[10px] text-[#5C5A55] mt-0.5">{bill.tenant.address}</div>}
          </div>
        </div>

        {/* Bill-to + status */}
        <div className="px-8 py-5 grid grid-cols-2 gap-6 border-b border-[#E5E2D9]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Bill To</div>
            <div className="font-medium">{bill.customer?.name}</div>
            {bill.customer?.customer_code && <div className="text-xs font-mono text-[#5C5A55]">{bill.customer.customer_code}</div>}
            {bill.customer?.phone && <div className="text-xs text-[#5C5A55]">{bill.customer.phone}</div>}
            {bill.customer?.address && <div className="text-xs text-[#5C5A55] mt-1">{bill.customer.address}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Status</div>
            {paid ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#2D5A27] text-white rounded-md text-sm font-medium">
                <CheckCircle size={14} weight="fill"/>Paid
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FDE7C3] text-[#7A5A11] rounded-md text-sm font-medium">
                <Clock size={14} weight="fill"/>Unpaid
              </span>
            )}
          </div>
        </div>

        {/* Service & issuance details */}
        <div className="px-8 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-[#E5E2D9] bg-[#FAFAF7]"
              data-testid="ebill-attribution">
          <Meta label="Service Date" v={bill.service_date || bill.date}/>
          <Meta label="Payment Due" v={bill.due_date}/>
          <Meta label="Field Staff"
                 v={bill.field_staff_name || (bill.field_staff_id ? "—" : "Unassigned")}
                 testid="ebill-field-staff"/>
          <Meta
            label="Bill Generated By"
            v={bill.created_by_name || "—"}
            sub={bill.created_by_role
              ? bill.created_by_role.replace("_", " ").toUpperCase()
              : null}
            testid="ebill-created-by"/>
        </div>

        {/* Items */}
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <tr>
              <th className="text-left px-8 py-3">Description</th>
              <th className="text-right px-4 py-3 w-20">Qty</th>
              <th className="text-right px-4 py-3 w-24">Rate</th>
              <th className="text-right px-8 py-3 w-32">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(bill.items || []).map((it, i) => {
              if (it.is_segment_header) {
                const sd = it.service_date || (it.label || "").replace(/^━\s*/, "").split(" · ")[0] || "";
                const fsName = it.field_staff_name
                  || ((it.label || "").includes(" · by ") ? (it.label || "").split(" · by ").pop() : "");
                const text = fsName
                  ? `${sd}  ·  Field staff: ${fsName}`
                  : (it.field_staff_id ? `${sd}  ·  Field staff: —` : sd);
                return (
                  <tr key={i} className="border-t-2 border-[#2D5A27]/20 bg-[#F4F8F2]"
                      data-testid={`ebill-segment-header-${i}`}>
                    <td colSpan={4} className="px-8 py-2 text-[11px] uppercase tracking-[0.2em] text-[#2D5A27] font-semibold">
                      {text}
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={i} className="border-t border-[#F0ECE3]">
                  <td className="px-8 py-3">
                    <div className="font-medium">{it.name || it.label || it.description}</div>
                    {it.transaction_type === "PAY" && <div className="text-[10px] text-[#2D5A27]">Customer payout (waste sale)</div>}
                  </td>
                  <td className="px-4 py-3 text-right">{it.qty || 1}</td>
                  <td className="px-4 py-3 text-right">₹{(it.rate ?? 0).toFixed(2)}</td>
                  <td className="px-8 py-3 text-right font-medium">₹{(it.amount ?? 0).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div className="px-8 py-5 border-t border-[#E5E2D9]">
          <div className="ml-auto max-w-xs space-y-2 text-sm">
            <Row k="Subtotal" v={bill.subtotal}/>
            <Row k={`GST @ ${bill.gst_rate}%`} v={bill.gst_amount}/>
            <div className="border-t border-[#E5E2D9] pt-2"/>
            <Row k="Total" v={bill.total} bold/>
            {bill.prepaid_amount > 0 && <Row k={`Prepaid at booking${bill.prepaid_payment_id ? ` (${bill.prepaid_payment_id})` : ""}`} v={-bill.prepaid_amount}/>}
            {bill.prepaid_amount > 0 && <Row k="Balance due" v={bill.balance_due ?? 0} bold/>}
          </div>
          {bill.total_in_words && (
            <div className="mt-3 pt-3 border-t border-[#F0ECE3] text-xs text-[#5C5A55] italic" data-testid="ebill-total-words">
              Total in words: <span className="text-[#2D2B26] not-italic font-medium">{bill.total_in_words}</span>
            </div>
          )}
        </div>

        {/* Payment trail (only when paid) */}
        {paid && (
          <div className="px-8 py-4 border-t border-[#E5E2D9] bg-[#F4F8F2]" data-testid="ebill-payment-trail">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} weight="fill" color="#2D5A27"/>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27]">Payment Verified</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <KV k="Method" v={(bill.payment_method || "—").toUpperCase()}/>
              <KV k="Txn No" v={bill.payment_txn_no || "—"} mono/>
              <KV k="Txn Date" v={bill.payment_txn_date || bill.paid_at?.slice(0,10) || "—"}/>
              <KV k="Staff in-charge" v={bill.payment_staff_in_charge_name || "—"}/>
              <KV k="Verified by" v={bill.payment_verified_by || "—"}/>
              <KV k="Verified at" v={bill.payment_verified_at?.replace("T", " ").slice(0,16) || "—"}/>
            </div>
          </div>
        )}

        {/* Footer band — UPI QR + invoice generated by + Pay Now */}
        <div className="px-8 py-5 border-t border-[#E5E2D9] grid grid-cols-3 gap-4 items-end bg-[#FAFAF7] rounded-b-md print:bg-white">
          {/* Bottom-left: UPI QR */}
          <div className="col-span-1" data-testid="ebill-upi-block">
            {bill.upi_qr_image ? (
              <div>
                <img src={bill.upi_qr_image} alt="UPI QR"
                     className="w-28 h-28 border border-[#E5E2D9] rounded-md bg-white object-contain p-1"
                     data-testid="ebill-upi-qr"/>
                <div className="text-[10px] text-[#5C5A55] mt-1">Scan to pay via UPI</div>
                {bill.upi_id && <div className="text-[10px] font-mono text-[#2D5A27]">{bill.upi_id}</div>}
                {bill.business_name_for_upi && <div className="text-[10px] text-[#5C5A55]">to {bill.business_name_for_upi}</div>}
              </div>
            ) : (
              <div className="text-[10px] text-[#8A8780] italic">No UPI QR configured</div>
            )}
          </div>

          {/* Center: Invoice generated by */}
          <div className="col-span-1 text-center text-[10px]">
            <div className="uppercase tracking-[0.2em] text-[#8A8780] mb-1">Invoice generated by</div>
            <div className="text-sm text-[#2D2B26]">{bill.created_by_name || "—"}</div>
            {bill.created_by_role && <div className="text-[#5C5A55]">{bill.created_by_role}</div>}
          </div>

          {/* Right: actions */}
          <div className="col-span-1 flex flex-col gap-2 items-end print:hidden">
            {showPayNow && (
              <button onClick={payNow} disabled={paying} data-testid="ebill-paynow"
                      className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center gap-1.5 w-full justify-center">
                <CreditCard size={14} weight="bold"/>{paying ? "Starting…" : `Pay ₹${Number(dueNow).toFixed(2)} Now`}
              </button>
            )}
            <a href={`${API}/ebill/${invoiceNo}/pdf`}
                target="_blank" rel="noopener noreferrer"
                data-testid="ebill-download-pdf"
                className="h-10 px-4 bg-white border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded-md text-xs font-medium inline-flex items-center gap-1.5 w-full justify-center">
              <Printer size={12} weight="bold"/>Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="ebill-print"
                    className="h-9 px-3 bg-transparent text-[#5C5A55] hover:text-[#2D2B26] rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 w-full justify-center">
              Print this page
            </button>
          </div>
        </div>

        <div className="px-8 py-3 border-t border-[#F0ECE3] text-[10px] text-[#8A8780] flex items-center gap-1.5 print:bg-white" data-testid="ebill-url">
          <Receipt size={11}/>{bill.ebill_url}
        </div>
      </div>

      <div className="max-w-3xl mx-auto mt-6 text-center text-[11px] text-[#8A8780] print:hidden">
        Powered by Rescigo Health · This is a public verifiable e-bill.
      </div>
    </div>
  );
}

const Row = ({ k, v, bold }) => (
  <div className={`flex justify-between ${bold ? "text-base font-semibold" : ""}`}>
    <span className="text-[#5C5A55]">{k}</span>
    <span className="font-mono">₹{(v ?? 0).toFixed(2)}</span>
  </div>
);

const KV = ({ k, v, mono }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</div>
    <div className={`text-[#2D2B26] ${mono ? "font-mono" : ""}`}>{v}</div>
  </div>
);

function Meta({ label, v, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
      <div className="text-sm font-medium text-[#1A1A1A] mt-1 break-words">{v || "—"}</div>
      {sub && <div className="text-[9px] uppercase tracking-[0.16em] text-[#2D5A27] mt-0.5 font-semibold">{sub}</div>}
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-sm text-[#5C5A55]">{children}</div>
);
